import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { Logger } from '@patsnap/tahiti-core';
import type { CommonEvidenceSearchService, CommonEvidenceSearchResult } from '../common-evidence-search-service';
import type {
    CcCommonEvidenceRequest,
    CcCommonEvidenceStatus,
    PollCommonEvidenceResultResponse,
    PollCommonEvidenceResultBatchResponse,
    PatentEvidenceData,
    JudgeResult,
} from '../../apis/novelty-search-pr-api';
import type { PatentProfileWithMatchedTechFeature } from '../../types';
import { NoveltySearchPrApi } from '../../apis/novelty-search-pr-api';
import { RetrievalResultReportModel } from '../../models/retrieval-result-report-model';
import { CurrentMatchedPatentModel } from '../../models/current-matched-patent-model';
import { CurrentTaskService } from '@/services/current-task/current-task-service';

/**
 * 轮询配置常量
 */
const POLLING_CONFIG = {
    /**
     * 最大重试次数
     */
    MAX_RETRIES: 200,
    /**
     * 轮询间隔（毫秒）
     */
    INTERVAL_MS: 2000,
} as const;

/**
 * 常见证据检索服务实现
 */
@injectable()
export class CommonEvidenceSearchServiceImpl implements CommonEvidenceSearchService {
    // eslint-disable-next-line max-params
    constructor(
        @inject(Logger)
        private readonly _logger: Logger,
        @inject(NoveltySearchPrApi)
        private readonly _noveltySearchPrApi: NoveltySearchPrApi,
        @inject(RetrievalResultReportModel)
        private readonly _retrievalResultReportModel: RetrievalResultReportModel,
        @inject(CurrentMatchedPatentModel)
        private readonly _currentMatchedPatentModel: CurrentMatchedPatentModel,
        @inject(CurrentTaskService)
        private readonly _currentTaskService: CurrentTaskService,
    ) {}

    /**
     * 执行常见证据检索
     */
    async searchCommonEvidence(): Promise<CommonEvidenceSearchResult> {
        try {
            this._logger.info('开始常见证据检索');
            await this.preheatFeatureExtraction();
            const request = this._buildRequest();
            const response = await this._noveltySearchPrApi.ccCommonEvidence(request);

            // 将公知证据数据存储到模型中
            // 新的响应格式是 Record<patent_id, PatentEvidenceData>
            // 使用增量合并逻辑，而不是直接覆盖
            this._retrievalResultReportModel.update((state) => {
                const updatedData = { ...state.patentCommonEvidenceData };

                // 遍历响应中的每个专利数据
                Object.entries(response).forEach(([patentId, newData]) => {
                    const oldData = updatedData[patentId];

                    if (oldData) {
                        // 如果已存在旧数据，使用增量合并
                        updatedData[patentId] = {
                            ...oldData,
                            status: newData.status, // status 直接覆盖
                            job_id: newData.job_id,
                            error_message: newData.error_message,
                            // judge_result 使用增量合并
                            judge_result: this._mergeJudgeResults(
                                oldData.judge_result,
                                newData.judge_result,
                            ),
                        };
                    }
                    else {
                        // 如果不存在旧数据，直接使用新数据
                        updatedData[patentId] = newData;
                    }
                });

                return {
                    ...state,
                    patentCommonEvidenceData: updatedData,
                };
            });

            this._logger.info('常见证据检索完成', {
                jobIdsCount: Object.keys(response).length,
            });

            // 启动轮询,获取每个专利的判断结果
            this._logger.info('开始轮询公知证据结果');
            await this._pollCommonEvidenceResults(Object.keys(response));

            // 统计匹配到公知证据的技术特征数量
            const matchedFeaturesCount = this._countMatchedFeatures();

            this._logger.info('公知证据检索全部完成', {
                matchedFeaturesCount,
            });

            return {
                matchedFeaturesCount,
            };
        }
        catch (error) {
            this._logger.error('常见证据检索失败', error);
            throw error;
        }
    }

    /**
     * 轮询公知证据结果
     * @param patentIds 需要轮询的专利ID列表
     * @description 使用批量轮询接口查询所有专利的公知证据结果
     */
    private async _pollCommonEvidenceResults(patentIds: string[]): Promise<void> {
        const taskId = this._currentTaskService.taskId;

        this._logger.info('开始批量轮询专利公知证据结果', {
            taskId,
            patentCount: patentIds.length,
        });

        try {
            await this._pollTask({
                pollFn: () => this._noveltySearchPrApi.getCommonEvidenceResultBatch({
                    task_id: taskId,
                    patent_ids: patentIds,
                }),
                shouldContinuePolling: (batchResult) =>
                    this._shouldContinueBatchPolling(patentIds, batchResult),
                onSuccess: (batchResult) =>
                    this._processBatchResults(patentIds, batchResult),
                taskDescription: '批量专利公知证据结果',
                taskId,
            });
        }
        catch (error) {
            this._logger.error('批量轮询专利公知证据结果失败', {
                taskId,
                patentCount: patentIds.length,
                error: error instanceof Error ? {
                    message: error.message,
                    stack: error.stack,
                } : String(error),
            });
            throw error;
        }
    }

    /**
     * 判断是否需要继续批量轮询
     * @param patentIds 专利ID列表
     * @param batchResult 批量轮询结果
     * @returns 是否需要继续轮询
     */
    private _shouldContinueBatchPolling(
        patentIds: string[],
        batchResult: PollCommonEvidenceResultBatchResponse,
    ): boolean {
        // 如果还有待轮询的专利，则继续轮询
        return batchResult.pending_patent_ids.length > 0;
    }

    /**
     * 处理批量轮询结果
     * @param patentIds 专利ID列表
     * @param batchResult 批量轮询结果
     */
    private _processBatchResults(
        patentIds: string[],
        batchResult: PollCommonEvidenceResultBatchResponse,
    ): void {
        // 更新所有已完成专利的结果
        let successCount = 0;
        let failedCount = 0;

        Object.entries(batchResult.results).forEach(([patentId, result]) => {
            // 检查是否失败
            if (this._isTaskFailed(result.status)) {
                failedCount++;
                this._logger.error('专利公知证据检索任务失败', {
                    patentId,
                    status: result.status,
                    errorMessage: result.error_message,
                });
            }
            else {
                this._updatePatentResult(patentId, result);
                successCount++;
                this._logger.info('专利公知证据结果获取成功', {
                    patentId,
                    resultCount: result.judge_result?.length || 0,
                });
            }
        });

        this._logger.info('批量专利公知证据结果更新完成', {
            total: patentIds.length,
            success: successCount,
            failed: failedCount,
            pending: batchResult.pending_patent_ids.length,
        });
    }

    /**
     * 判断任务是否完成
     * @param status 任务状态
     */
    private _isTaskCompleted(status: CcCommonEvidenceStatus): boolean {
        return status === 'Success';
    }

    /**
     * 判断任务是否失败
     * @param status 任务状态
     */
    private _isTaskFailed(status: CcCommonEvidenceStatus): boolean {
        return status === 'Error';
    }

    /**
     * 判断特征提取任务是否完成
     * @param status 任务状态
     */
    private _isFeatureExtractionCompleted(status: string): boolean {
        return ['Success', 'Done', 'Completed', 'success', 'done', 'completed'].includes(status);
    }

    /**
     * 判断特征提取任务是否失败
     * @param status 任务状态
     */
    private _isFeatureExtractionFailed(status: string): boolean {
        return ['Failed', 'Error', 'failed', 'error'].includes(status);
    }

    /**
     * 增量合并 judge_result 数据
     * @param oldResults 旧的 judge_result 数据
     * @param newResults 新的 judge_result 数据
     * @returns 合并后的 judge_result 数据
     *
     * 合并规则：
     * 1. 旧数据一律不更新
     * 2. 仅追加 comparison_feature 不重复的新数据
     */
    private _mergeJudgeResults(
        oldResults: JudgeResult[] | undefined,
        newResults: JudgeResult[] | undefined,
    ): JudgeResult[] | undefined {
        // 如果没有新数据，返回旧数据
        if (!newResults || newResults.length === 0) {
            return oldResults;
        }

        // 如果没有旧数据，直接返回新数据
        if (!oldResults || oldResults.length === 0) {
            return newResults;
        }

        const existingFeatures = new Set(
            oldResults.map((result) => result.comparison_feature),
        );

        const appendedResults: JudgeResult[] = [];
        newResults.forEach((newResult) => {
            if (!existingFeatures.has(newResult.comparison_feature)) {
                existingFeatures.add(newResult.comparison_feature);
                appendedResults.push(newResult);
            }
        });

        return oldResults.concat(appendedResults);
    }

    /**
     * 更新专利的判断结果
     * @param patentId 专利ID
     * @param result 轮询结果
     */
    private _updatePatentResult(
        patentId: string,
        result: PollCommonEvidenceResultResponse,
    ): void {
        this._retrievalResultReportModel.update((state) => {
            const updatedData = { ...state.patentCommonEvidenceData };
            if (updatedData[patentId]) {
                const oldData = updatedData[patentId];
                // 使用增量合并逻辑更新 judge_result
                const mergedJudgeResult = this._mergeJudgeResults(
                    oldData.judge_result,
                    result.judge_result,
                );

                updatedData[patentId] = {
                    ...oldData,
                    status: result.status, // status 直接覆盖
                    error_message: result.error_message,
                    judge_result: mergedJudgeResult, // 使用合并后的结果
                };
            }
            return {
                ...state,
                patentCommonEvidenceData: updatedData,
            };
        });
    }

    /**
     * 延迟执行
     * @param ms 延迟毫秒数
     */
    private _delay(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    /**
     * 通用轮询方法
     * @template T 轮询结果类型
     * @param options 轮询选项
     * @param options.pollFn 轮询函数
     * @param options.shouldContinuePolling 判断是否需要继续轮询的回调函数，返回true继续轮询，返回false结束轮询
     * @param options.onSuccess 成功回调
     * @param options.taskDescription 任务描述（用于日志）
     * @param options.taskId 任务ID（用于日志）
     * @param options.maxRetries 最大重试次数
     * @param options.intervalMs 轮询间隔（毫秒）
     * @description 通用轮询逻辑，持续轮询直到任务完成、失败或超时
     */
    // eslint-disable-next-line max-lines-per-function
    private async _pollTask<T>(options: {
        pollFn: () => Promise<T>;
        shouldContinuePolling: (result: T) => boolean;
        onSuccess: (result: T) => void;
        taskDescription: string;
        taskId: string;
        maxRetries?: number;
        intervalMs?: number;
    }): Promise<void> {
        const {
            pollFn,
            shouldContinuePolling,
            onSuccess,
            taskDescription,
            taskId,
            maxRetries = POLLING_CONFIG.MAX_RETRIES,
            intervalMs = POLLING_CONFIG.INTERVAL_MS,
        } = options;

        let retryCount = 0;

        this._logger.info(`开始轮询${taskDescription}`, {
            taskId,
            maxRetries,
            intervalMs,
        });

        while (retryCount < maxRetries) {
            try {
                const result = await pollFn();

                this._logger.info(`${taskDescription}轮询响应`, {
                    taskId,
                    retryCount,
                });

                // 使用回调函数判断是否需要继续轮询
                const shouldContinue = shouldContinuePolling(result);

                if (!shouldContinue) {
                    // 任务完成
                    onSuccess(result);

                    this._logger.info(`${taskDescription}轮询成功`, {
                        taskId,
                        retryCount,
                    });

                    return; // 轮询成功,退出
                }

                // 任务还在进行中
                this._logger.info(`${taskDescription}进行中,继续轮询`, {
                    taskId,
                    retryCount,
                });

                await this._delay(intervalMs);
                retryCount++;
            }
            catch (error) {
                this._logger.error(`${taskDescription}轮询请求失败`, {
                    taskId,
                    retryCount,
                    error: error instanceof Error ? {
                        message: error.message,
                        stack: error.stack,
                    } : String(error),
                });

                // 直接抛出错误，不再重试
                throw error;
            }
        }

        // 超过最大重试次数
        throw new Error(`${taskDescription}轮询超时: 任务 ${taskId} 超过最大重试次数 ${maxRetries}`);
    }

    /**
     * 预热特征拆解&语义检索
     * @description 调用搜索特征提取接口，预热后端服务，加速后续的公知证据检索
     */
    async preheatFeatureExtraction(): Promise<void> {
        try {
            const reportData = this._retrievalResultReportModel.modelData;
            if (reportData.patentCommonEvidenceData && Object.keys(reportData.patentCommonEvidenceData).length) {
                return;
            }
            // 过滤出用户选中的技术特征
            const selectedFeatures = reportData.techFeatures.filter((f) => f.selected);

            if (selectedFeatures.length === 0) {
                this._logger.warn('没有选中的技术特征，跳过预热');
                return;
            }

            // 获取当前任务ID
            const taskId = this._currentTaskService.taskId;

            this._logger.info('开始预热拆解特征', {
                taskId,
                featureCount: selectedFeatures.length,
            });

            // 调用预热接口（根据新的 API 定义，只需传递 task_id）
            // 这会创建一个异步任务
            const response = await this._noveltySearchPrApi.searchFeatureExtract({
                task_id: taskId,
            });

            this._logger.info('特征拆解任务已创建', {
                searchKeysCount: response.search_keys?.length || 0,
            });

            // 轮询检查任务是否完成
            await this._pollFeatureExtractionResult(taskId);

            this._logger.info('预热拆解特征完成', { taskId });
        }
        catch (error) {
            // 预热失败不应该阻塞检索流程，只记录错误
            this._logger.error('预热拆解特征失败', error);
        }
    }

    /**
     * 轮询特征拆解结果
     * @param taskId 任务ID
     * @param maxRetries 最大重试次数
     * @param intervalMs 轮询间隔（毫秒）
     * @description 持续轮询直到任务完成、失败或超时
     */
    private async _pollFeatureExtractionResult(
        taskId: string,
        maxRetries: number = POLLING_CONFIG.MAX_RETRIES,
        intervalMs: number = POLLING_CONFIG.INTERVAL_MS,
    ): Promise<void> {
        return this._pollTask({
            pollFn: () => this._noveltySearchPrApi.searchFeatureExtractResult({
                task_id: taskId,
            }),
            shouldContinuePolling: (result) => {
                // 如果没有 status 字段，说明任务已完成，不再继续轮询
                if (result.status === undefined) {
                    return false;
                }
                // 如果任务失败，抛出错误
                if (this._isFeatureExtractionFailed(result.status)) {
                    throw new Error(`特征拆解任务失败: ${result.status}`);
                }
                // 如果任务完成，不再继续轮询
                if (this._isFeatureExtractionCompleted(result.status)) {
                    return false;
                }
                // 继续轮询
                return true;
            },
            onSuccess: () => {
                this._logger.info('特征拆解任务完成', { taskId });
            },
            taskDescription: '特征拆解任务',
            taskId,
            maxRetries,
            intervalMs,
        });
    }

    /**
     * 构建API请求数据
     * 根据新的 API 规范，只需传递 task_id 和 patent_ids
     * 其他数据（如 lang, cc_result, end_date）由后端从数据库查询
     */
    private _buildRequest(): CcCommonEvidenceRequest {
        const currentMatchedPatentState = this._currentMatchedPatentModel.modelData;
        const reportData = this._retrievalResultReportModel.modelData;
        const patentCommonEvidenceData = reportData.patentCommonEvidenceData;

        // 获取选中的专利ID列表，并且该专利的匹配技术特征中包含类型为 "obvious" 的特征
        // 同时过滤掉已经查询过公知证据的专利
        const selectedPatentIds = currentMatchedPatentState.ranking
            .filter((p) => this._shouldRequestCommonEvidenceForPatent(p, patentCommonEvidenceData))
            .map((p) => p.patentId);

        this._logger.info('构建常见证据检索请求', {
            taskId: this._currentTaskService.taskId,
            patentCount: selectedPatentIds.length,
        });

        return {
            task_id: this._currentTaskService.taskId,
            patent_ids: selectedPatentIds,
        };
    }

    /**
     * 判断是否应该为指定专利请求公知证据数据
     * @param patent 专利信息
     * @param patentCommonEvidenceData 公知证据数据映射
     * @returns 是否应该请求公知证据
     */
    private _shouldRequestCommonEvidenceForPatent(
        patent: PatentProfileWithMatchedTechFeature,
        patentCommonEvidenceData: Record<string, PatentEvidenceData>,
    ): boolean {
        // 检查专利是否被选中
        if (!patent.selected) {
            return false;
        }

        // 获取所有选中的技术特征
        const reportData = this._retrievalResultReportModel.modelData;
        const selectedTechFeatures = reportData.techFeatures.filter((f) => f.selected);

        // 创建技术特征名称到特征对象的映射（用于快速查找 featureWeight）
        const techFeatureMap = new Map(selectedTechFeatures.map((f) => [f.name, f]));

        // 获取该专利所有 obvious 类型且非主题特征的技术特征名称
        const obviousFeatureNames = patent.matchedTechFeatures
            .filter((feature) => {
                // 必须是 obvious 类型
                if (feature.type !== 'obvious') {
                    return false;
                }
                // 查找对应的技术特征，检查是否为主题特征
                const techFeature = techFeatureMap.get(feature.techFeature);
                // 过滤掉主题特征（featureWeight === 'topic'）
                return techFeature && techFeature.featureWeight !== 'topic';
            })
            .map((feature) => feature.techFeature);

        // 如果没有符合条件的 obvious 特征，不需要查询
        if (obviousFeatureNames.length === 0) {
            return false;
        }

        // 检查是否已经查询过公知证据（判断 judge_result 中是否包含所有 obvious 特征的数据）
        const existingData = patentCommonEvidenceData[patent.patentId];
        if (existingData?.judge_result) {
            // 获取已存在的技术特征名称列表
            const existingFeatureNames = existingData.judge_result.map((r) => r.tech_feature);
            // 检查是否所有 obvious 特征都已有记录
            const allFeaturesHaveRecords = obviousFeatureNames.every((name) =>
                existingFeatureNames.includes(name),
            );
            if (allFeaturesHaveRecords) {
                return false;
            }
        }

        return true;
    }

    /**
     * 统计匹配到公知证据的技术特征数量
     * @description 统计所有专利的 judge_result 中 judge 为 true 的数量之和
     * @returns 匹配的技术特征总数
     */
    private _countMatchedFeatures(): number {
        const reportData = this._retrievalResultReportModel.modelData;
        const patentCommonEvidenceData = reportData.patentCommonEvidenceData;

        let totalCount = 0;

        // 遍历所有专利的公知证据数据
        Object.values(patentCommonEvidenceData).forEach((evidenceData) => {
            // 只统计成功状态的专利
            if (evidenceData.status === 'Success' && evidenceData.judge_result) {
                // 累加该专利中 judge 为 true 的 judge_result 数量
                const matchedCount = evidenceData.judge_result.filter((result) => result.judge === true).length;
                totalCount += matchedCount;
            }
        });

        this._logger.info('统计公知证据匹配结果', {
            totalPatents: Object.keys(patentCommonEvidenceData).length,
            matchedFeaturesCount: totalCount,
        });

        return totalCount;
    }

    /**
     * 加载公知证据最终结果
     * @description 从数据库中加载已完成的公知证据检索结果，并存储到模型中
     */
    async loadFinalCommonEvidenceResult(): Promise<void> {
        try {
            const taskId = this._currentTaskService.taskId;

            this._logger.info('开始加载公知证据最终结果', { taskId });

            // 调用新接口获取最终结果
            const response = await this._noveltySearchPrApi.getFinalCommonEvidenceResult({
                task_id: taskId,
            });

            // 检查是否有数据
            if (!response || Object.keys(response).length === 0) {
                this._logger.info('没有找到公知证据最终结果', { taskId });
                return;
            }

            // 将结果存储到模型中
            this._retrievalResultReportModel.update((state) => {
                const updatedData = { ...state.patentCommonEvidenceData };

                // 更新每个专利的证据数据
                Object.entries(response).forEach(([patentId, evidenceData]) => {
                    updatedData[patentId] = evidenceData;
                });

                return {
                    ...state,
                    patentCommonEvidenceData: updatedData,
                };
            });

            this._logger.info('公知证据最终结果加载完成', {
                taskId,
                patentCount: Object.keys(response).length,
            });
        }
        catch (error) {
            // 加载失败不应该阻塞页面渲染，只记录错误
            this._logger.error('加载公知证据最终结果失败', error);
        }
    }
}
